import Vue from "vue";

import { getDownloadUrl } from "@/data/repository/FirebaseImageRepository";
import { parseRoomUsageOptions } from "@/utils/RoomUsageOptions";

/**
 * The room usage options per Beschreibung version, reactive so a room label re-renders once its
 * version has loaded. `null` = the version has no room usage options to read.
 */
const state = Vue.observable({ optionsByVersion: {} as Record<string, string[] | null> });

/** One download per version and session, however many components ask for it. */
const loadsByVersion: Record<string, Promise<void>> = {};

/**
 * The loaded room usage options of a Beschreibung version, see {@link parseRoomUsageOptions}.
 * Reactive: a computed reading this re-evaluates when {@link loadRoomUsageOptions} completes.
 *
 * @param version the project's pinned `damagePatternData.roomInfoConfigVersion`
 * @returns the options, or `null` while loading, without a version, or when there are none
 */
export function getRoomUsageOptions(version?: string | null): string[] | null {
  const optionsByVersion = state.optionsByVersion;

  return version ? optionsByVersion[version] ?? null : null;
}

/**
 * Downloads and parses a Beschreibung version's room usage options, once per session. Readable by
 * every signed-in user (`roomInfoConfig/{configName}` in the Storage rules).
 *
 * @param version the project's pinned `damagePatternData.roomInfoConfigVersion`
 */
export function loadRoomUsageOptions(version?: string | null): Promise<void> {
  if (!version) {
    return Promise.resolve();
  }

  if (!loadsByVersion[version]) {
    loadsByVersion[version] = (async () => {
      try {
        const response = await fetch(await getDownloadUrl(`roomInfoConfig/info.${version}.csv`));

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        Vue.set(state.optionsByVersion, version, parseRoomUsageOptions(await response.text()));
      } catch {
        // Not worth an error: the room keeps its bare number. Not retried in this session, or every
        // logbook row asking for the same missing version would download it again.
      }
    })();
  }

  return loadsByVersion[version];
}
